/** /reflect - lightweight end-of-day reflection capture. One entry
 *  per day (upserts). No emojis: mood is a labelled dot-chip picker,
 *  energy is a 1-5 slider. On save the reflection also flows into
 *  packets so Memso indexes + recalls it. */
import { TeachingBanner } from "../components/TeachingBanner";
import { WikiAutocompleteTextarea } from "../components/WikiAutocompleteTextarea";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Moon } from "lucide-react";
import {
  api,
  type ReflectionRow,
  type ReflectionStats,
} from "../lib/api";

const MOODS: Array<{
  key: "great" | "good" | "neutral" | "low" | "rough";
  label: string;
  dot: string;
}> = [
  { key: "great", label: "Great", dot: "bg-emerald-500" },
  { key: "good", label: "Good", dot: "bg-lime-500" },
  { key: "neutral", label: "Neutral", dot: "bg-slate-400" },
  { key: "low", label: "Low", dot: "bg-amber-500" },
  { key: "rough", label: "Rough", dot: "bg-rose-500" },
];

const MOOD_DOT: Record<string, string> = Object.fromEntries(
  MOODS.map((m) => [m.key, m.dot]),
);

export function Reflect() {
  const [mood, setMood] = useState<string | null>(null);
  const [energy, setEnergy] = useState(3);
  const [highlights, setHighlights] = useState("");
  const [lows, setLows] = useState("");
  const [grateful, setGrateful] = useState("");
  const [tomorrow, setTomorrow] = useState("");
  const [openLoops, setOpenLoops] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [week, setWeek] = useState<ReflectionRow[]>([]);
  const [stats, setStats] = useState<ReflectionStats | null>(null);

  const load = useCallback(async () => {
    try {
      const [today, list, st] = await Promise.all([
        api.reflectionToday(),
        api.reflectionsList(),
        api.reflectionStats(30),
      ]);
      if (today.reflection) {
        const r = today.reflection;
        setMood(r.mood);
        setEnergy(r.energy_level ?? 3);
        setHighlights(r.highlights_md ?? "");
        setLows(r.lows_md ?? "");
        setGrateful(r.grateful_for_md ?? "");
        setTomorrow(r.tomorrow_focus_md ?? "");
        setOpenLoops(r.open_loops_md ?? "");
      }
      setWeek(list.reflections.slice(0, 7));
      setStats(st);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await api.saveReflection({
        mood,
        energy_level: energy,
        highlights_md: highlights,
        lows_md: lows,
        grateful_for_md: grateful,
        tomorrow_focus_md: tomorrow,
        open_loops_md: openLoops,
      });
      setSaved(true);
      setHint(
        res.engagement_hint
          ? `An open loop looks related to your engagement "${res.engagement_hint.title}".`
          : null,
      );
      window.setTimeout(() => setSaved(false), 4000);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const Field = ({
    label,
    value,
    onChange,
    placeholder,
  }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
  }) => (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <WikiAutocompleteTextarea
        rows={2}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </label>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <TeachingBanner id="reflections_overview" />
      <div className="flex items-center gap-2">
        <Moon className="w-5 h-5 text-indigo-500" />
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          How was today?
        </h1>
      </div>

      <div>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Mood
        </span>
        <div className="mt-1 flex flex-wrap gap-2">
          {MOODS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMood(m.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                mood === m.key
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-200"
                  : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
              }`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} />
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Energy: {energy}/5
        </span>
        <input
          type="range"
          min={1}
          max={5}
          value={energy}
          onChange={(e) => setEnergy(Number(e.target.value))}
          className="mt-1 w-full accent-indigo-500"
        />
      </div>

      <div className="space-y-3">
        <TeachingBanner id="wiki_autocomplete_tip" />
        <Field
          label="Highlights"
          value={highlights}
          onChange={setHighlights}
          placeholder="What went well today?"
        />
        <Field
          label="Lows"
          value={lows}
          onChange={setLows}
          placeholder="What was hard?"
        />
        <Field
          label="Grateful for"
          value={grateful}
          onChange={setGrateful}
          placeholder="One or two gratitudes"
        />
        <Field
          label="Tomorrow's focus"
          value={tomorrow}
          onChange={setTomorrow}
          placeholder="One thing for tomorrow"
        />
        <Field
          label="Open loops"
          value={openLoops}
          onChange={setOpenLoops}
          placeholder="Anything unfinished or on your mind?"
        />
      </div>

      {err && (
        <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={submit}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saved ? <Check className="w-4 h-4" /> : null}
          {saved ? "Saved. Rest well." : saving ? "Saving..." : "Save reflection"}
        </button>
        <Link
          to="/reflect/history"
          className="text-sm text-indigo-600 dark:text-indigo-300 hover:underline"
        >
          View past reflections
        </Link>
      </div>

      {hint && (
        <div className="rounded-lg bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          {hint}
        </div>
      )}

      <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            This week
          </span>
          {stats && (
            <span className="text-xs text-slate-500">
              Streak: {stats.streak} day{stats.streak === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="mt-2 flex gap-2">
          {week.length === 0 && (
            <span className="text-xs text-slate-400">
              No reflections yet this week.
            </span>
          )}
          {week.map((w) => (
            <div
              key={w.id}
              title={`${w.reflection_date}: ${w.mood ?? "no mood"}`}
              className={`h-4 w-4 rounded-full ${
                w.mood ? MOOD_DOT[w.mood] ?? "bg-slate-300" : "bg-slate-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
